import { useMemo } from "react";

import { filterTruthy, lamportsToUiAmount, Result } from "@bridgesplit/utils";
import { formatDurationWithTypeShorthand } from "@bridgesplit/abf-sdk";
import { BsMetaUtil, isStrategyDurationSame, TokenRestrictedActions, useOraclePrices } from "@bridgesplit/abf-react";
import { TagTextAlert } from "@bridgesplit/ui";
import { AppDialog, useAppDialog } from "app/utils";
import { TransactionTypes } from "app/types";
import { usePermissionedLoanCheck, useTokenActionRestrictionsConfig } from "app/hooks";
import { TokenRestrictionAlerts } from "app/components/tokens";

import { AppButton, BORROW_APY_ABOVE_AVG_THRESHOLD, useBalanceChecker } from "../../common";
import { useMarketBorrow, useMarketCapDetails, useNapoleonErrors, useNapoleonFetching } from "../util";
import { useMarketBorrowContext } from "./MarketBorrowContext";
import { useBorrowStats } from "./util";
import { useMarketContext } from "../common";

export default function BorrowCta() {
    const borrow = useMarketBorrow();
    const { open: openDialog } = useAppDialog();
    const {
        form: { collateralMint, strategyDuration },
        bestQuote
    } = useMarketBorrowContext();
    const { token, principalStats } = useMarketContext();

    const alertError = useAlertError();
    const ctaError = useCtaError();

    const tokenRestrictionsConfig = useTokenActionRestrictionsConfig({
        mints: [token?.mint, collateralMint].filter(filterTruthy),
        primaryRelevantAction: TokenRestrictedActions.Borrow,
        isActive: true
    });

    const borrowApy = bestQuote?.apy;
    const avgApy = useMemo(() => {
        const bucket = principalStats?.principalStats.durationToWAvgBorrowApy.find((b) =>
            isStrategyDurationSame({ duration: b.duration, durationType: b.durationType }, strategyDuration)
        );
        return bucket?.wAvgApy;
    }, [principalStats, strategyDuration]);

    const borrowApyAboveAvg =
        borrowApy !== undefined &&
        avgApy !== undefined &&
        avgApy > 0 &&
        borrowApy > avgApy * (1 + BORROW_APY_ABOVE_AVG_THRESHOLD);

    const onCtaClick = () => {
        if (borrowApyAboveAvg) {
            openDialog(AppDialog.BorrowRiskConfirm, {
                netApyPct: undefined,
                borrowApy,
                avgApy,
                negativeNetApy: false,
                borrowApyAboveAvg: true,
                onConfirm: borrow
            });
            return Promise.resolve(Result.ok());
        }
        return borrow();
    };

    return (
        <>
            <AppButton
                isTransaction={!borrowApyAboveAvg}
                transactionType={TransactionTypes.MarketBorrow}
                asyncCta={{
                    onClickWithResult: onCtaClick,
                    options: { alertOnError: true }
                }}
                color="secondary"
                disabled={!!alertError || !!ctaError}
            >
                {ctaError || "Borrow"}
            </AppButton>
            {!!alertError && (
                <TagTextAlert color="warning" textSx={{ display: "inline-block" }} icon="warning">
                    {alertError}
                </TagTextAlert>
            )}
            {tokenRestrictionsConfig && <TokenRestrictionAlerts tokenRestrictionConfig={tokenRestrictionsConfig} />}
        </>
    );
}

function useCtaError() {
    const {
        form: { amount, collateralAmount, strategyDuration, collateralMint }
    } = useMarketBorrowContext();
    const { token } = useMarketContext();
    const isFetching = useNapoleonFetching();

    const tokenRestrictionsConfig = useTokenActionRestrictionsConfig({
        mints: [token?.mint, collateralMint].filter(filterTruthy),
        primaryRelevantAction: TokenRestrictedActions.Borrow,
        isActive: true
    });

    return useMemo(() => {
        if (tokenRestrictionsConfig?.activeRestrictionCta) return tokenRestrictionsConfig.activeRestrictionCta;
        if (isFetching) return "Loading offers...";
        if (!collateralAmount) return "No collateral selected";
        if (!amount) return "Enter an amount to borrow";
        if (!strategyDuration) return "Select duration";

        return null;
    }, [amount, collateralAmount, isFetching, strategyDuration, tokenRestrictionsConfig?.activeRestrictionCta]);
}

function useAlertError() {
    const {
        form,
        bestQuote,
        allDurationQuotes,
        activeUserCollateral,
        maxQuote,
        twapPrices: prices,
        quoteFetching
    } = useMarketBorrowContext();
    const queryError = useQueryError();

    const { insufficientBalance } = useBalanceChecker({
        mint: form.collateralMint ?? undefined,
        amount: form.collateralAmount,
        customBalance: activeUserCollateral?.amount
    });

    const borrowCapDetails = useMarketCapDetails();
    const { insufficientCollateral } = useBorrowStats();
    const { token } = useMarketContext();
    const isFetching = useNapoleonFetching();
    const { isLoading: oracleLoading } = useOraclePrices(undefined);

    const principalSymbol = BsMetaUtil.getSymbol(token);
    const collateralSymbol = BsMetaUtil.getSymbol(activeUserCollateral?.metadata);

    // Check permissioned RWA token permissions
    const { permissionDenied, permissionMessage } = usePermissionedLoanCheck(token, activeUserCollateral?.metadata);

    return useMemo(() => {
        if (permissionDenied) return permissionMessage;
        if (queryError) return queryError;
        if (isFetching || quoteFetching) return undefined;
        if (borrowCapDetails?.remainingGlobalCapacity === 0) return `${principalSymbol} borrow cap exceeded`;
        if (!oracleLoading && token && (form.collateralAmount || form.amount)) {
            if (prices.principalOracle === null) return `No price found for ${principalSymbol}`;
            if (!prices.collateralOracle && !!activeUserCollateral) return `No price found for ${collateralSymbol}`;
        }

        if (!form.amount || !form.strategyDuration) return undefined;
        if (!bestQuote) {
            if (!!maxQuote?.maxBorrow && form.amount > maxQuote?.maxBorrow) {
                if (form.amount > maxQuote.principalAvailable)
                    return `Market doesn't have enough ${principalSymbol} supplied`;
                return "Exceeds your borrow limit";
            }
            if (allDurationQuotes?.length === 0)
                return `No offers for ${formatDurationWithTypeShorthand(form.strategyDuration)} duration`;
            return "No matching offers";
        }

        if (!form.collateralAmount) return undefined;
        if (insufficientBalance) return "Insufficient collateral balance";
        if (borrowCapDetails) {
            const { exceedsGlobalCap, exceedsPerLoanCap, borrowCap } = borrowCapDetails;
            if (exceedsPerLoanCap(form.amount))
                return `Exceeds ${BsMetaUtil.formatAmount(
                    token,
                    lamportsToUiAmount(borrowCap?.perLoan, token?.decimals)
                )} per loan`;
            if (exceedsGlobalCap(form.amount)) return "Exceeds borrow cap";
        }

        if (insufficientCollateral) return "Exceeds your borrow limit";
        return null;
    }, [
        activeUserCollateral,
        allDurationQuotes?.length,
        bestQuote,
        borrowCapDetails,
        collateralSymbol,
        form.amount,
        form.collateralAmount,
        form.strategyDuration,
        insufficientBalance,
        insufficientCollateral,
        isFetching,
        maxQuote?.maxBorrow,
        maxQuote?.principalAvailable,
        oracleLoading,
        prices.collateralOracle,
        prices.principalOracle,
        principalSymbol,
        queryError,
        quoteFetching,
        token,
        permissionDenied,
        permissionMessage
    ]);
}

function useQueryError() {
    const quoteErrors = useNapoleonErrors();
    const { isError: pricingError } = useOraclePrices(undefined);

    if (pricingError) {
        return "Unable to fetch the latest prices";
    }

    if (quoteErrors) {
        return "Unable to fetch quote";
    }

    return null;
}
